// WebSocket client for the communications hub.
// open() → connect, exponential reconnect, watched-aware close, single
// in-flight connection.
//
// Two things are the app's, injected at construction: how a socket is opened
// for the site's path (the web lets the browser attach the httpOnly session
// cookie to the upgrade; mobile passes a Bearer header), and what "watched"
// means (`document.hidden` on the web, app state plus tab focus on mobile),
// reported through `setWatched`. If the session has expired the handshake is
// rejected and `onclose` drives the normal reconnect/backoff.

import type { ChatStatus, ClientFrame, ServerFrame } from '../interfaces/ICommsApi.js';

const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const HIDDEN_CLOSE_DELAY_MS = 5 * 60_000;

export interface ChatSocketHandlers {
  onFrame: (frame: ServerFrame) => void;
  onStatus: (status: ChatStatus) => void;
}

export interface IChatSocketOptions {
  /**
   * Opens the platform socket for the site's socket path
   * (`/api/sites/:siteId/comms/ws`); the app resolves the base URL, the
   * scheme and the credentials.
   */
  createSocket: (path: string) => WebSocket;
}

export class ChatSocket {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelayMs = RECONNECT_INITIAL_MS;
  private closed = false;
  private status: ChatStatus = 'closed';

  constructor(
    private readonly siteId: string,
    private readonly handlers: ChatSocketHandlers,
    private readonly options: IChatSocketOptions,
  ) {}

  open(): void {
    if (this.closed) return;
    if (this.ws) return; // already connecting or open

    this.setStatus(this.reconnectDelayMs === RECONNECT_INITIAL_MS ? 'connecting' : 'reconnecting');

    // No token in the path — the app's socket factory carries the credentials.
    const ws = this.options.createSocket(`/api/sites/${encodeURIComponent(this.siteId)}/comms/ws`);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelayMs = RECONNECT_INITIAL_MS;
      this.setStatus('open');
    };

    ws.onmessage = (ev) => {
      let parsed: ServerFrame;
      try {
        parsed = JSON.parse(ev.data as string) as ServerFrame;
      } catch {
        // Malformed server frame — should never happen with our own backend.
        return;
      }
      if (parsed.type === 'token_expired') {
        // The server-side session behind this socket lapsed. Drop the dead
        // connection and reconnect; the handshake re-sends the current session
        // credentials. If the whole session is gone the reconnect keeps failing
        // and a data-call 401 elsewhere ends the session.
        this.softClose();
        this.scheduleReconnect();
        return;
      }
      this.handlers.onFrame(parsed);
    };

    ws.onclose = () => {
      this.softClose();
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onerror fires before onclose; let onclose drive the reconnect so we
      // don't double-schedule.
    };
  }

  /** Writes the frame; false when the socket isn't open and nothing was sent. */
  send(frame: ClientFrame): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(frame));
    return true;
  }

  close(): void {
    this.closed = true;
    this.softClose();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.hiddenCloseTimer) clearTimeout(this.hiddenCloseTimer);
    this.setStatus('closed');
  }

  /**
   * Whether anyone is looking: an unwatched socket is closed after a grace
   * period, and a watched one is reopened if it had closed.
   */
  setWatched(watched: boolean): void {
    if (!watched) {
      // Cancel any pending reconnect — no point opening a connection that
      // the close-timer will tear down moments later anyway.
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.hiddenCloseTimer) clearTimeout(this.hiddenCloseTimer);
      this.hiddenCloseTimer = setTimeout(() => {
        this.softClose();
      }, HIDDEN_CLOSE_DELAY_MS);
    } else {
      if (this.hiddenCloseTimer) {
        clearTimeout(this.hiddenCloseTimer);
        this.hiddenCloseTimer = null;
      }
      if (!this.ws && !this.closed) void this.open();
    }
  }

  private softClose(): void {
    if (this.ws) {
      // Detach handlers before close() so the close event we trigger here
      // doesn't itself schedule another reconnect.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      try {
        this.ws.close();
      } catch {
        // ignore — already closed or never opened
      }
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.setStatus('reconnecting');
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => { void this.open(); }, delay);
  }

  private setStatus(next: ChatStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.handlers.onStatus(next);
  }
}
